import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import TicketDetails from "../components/TicketDetails";
import Layout from "../components/Layout/Layout";
import TicketForm from "../components/TicketForm";
import { PlusIcon } from "@heroicons/react/24/solid"; // or /outline for outline version
import { XMarkIcon } from "@heroicons/react/24/solid";
import Select from "react-select";
import Dashboard from "./Dashboard";

import api from "../utils/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from "chart.js";
ChartJS.register(
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale
);
import TicketDashboardSummary from "../components/TicketDashboardSummary";
import TicketTable from "../components/TicketTable";
import Loader from "../components/Loader";
import { FiChevronDown, FiChevronUp, FiCalendar, FiChevronLeft, FiChevronRight, FiRotateCcw } from "react-icons/fi";
import {
  getStatusColor,
  getStatusTextColor,
  getStatusIcon,
  getStatusLabel,
} from "../utils/statusStyles";
import { useUser } from "../contexts/UserContext";
import { useClient } from "../contexts/ClientContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TicketSchedulerTable from "../components/TicketScheduler/TicketSchedulerTable";
import TicketSchedulerModal from "../components/TicketScheduler/TicketSchedulerModal";

import { format } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowPathIcon } from "@heroicons/react/24/solid";

export const STATIC_PRIORITY_OPTIONS = [
  { value: "1", label: "Critical" },
  { value: "2", label: "High" },
  { value: "3", label: "Medium" },
  { value: "7", label: "Low" },
  { value: "4", label: "To Be Done In Future" },
];

export const STATIC_STATUS_OPTIONS = [
  { value: "1", label: "New" },
  { value: "4", label: "IN process" },
  { value: "3", label: "Pending verification & closure" },
  { value: "5", label: "Closed" },
  { value: "6", label: "Reopen" },
  { value: "8", label: "Testing" },
  { value: "9", label: "Testing Approved" },
  { value: "10", label: "Testing Rejected" },
  { value: "12", label: "Pending Customer Verification & Confirmation" },
  { value: "13", label: "On Hold" },
  { value: "14", label: "Code Review Request" },
  { value: "15", label: "Code Review Approved" },
  { value: "16", label: "Code Review Rejected" },
  { value: "17", label: "Production Move Request" },
  { value: "18", label: "Production Moved" },
  { value: "19", label: "Production Tested" },
  { value: "20", label: "Production Rejected" },
];

export default function TicketListPage() {
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  const ticketIdFromUrl = searchParams.get("tid");
  const { selectedClientId, setSelectedClientId, clients } = useClient();
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicketChildId, setSelectedTicketChildId] = useState(null);
  const [ticketViewIndex, setTicketViewIndex] = useState(-1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem("ticketRowsPerPage");
    return saved ? parseInt(saved, 10) : 10;
  });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedCustomerChildId, setSelectedCustomerChildId] = useState(null);
  const fromDateInputRef = useRef(null);
  const toDateInputRef = useRef(null);
  const [minTicketDate, setMinTicketDate] = useState(null);
  const [maxTicketDate, setMaxTicketDate] = useState(null);
  const safeMinDate = minTicketDate ?? new Date(new Date().getFullYear() - 1, 0, 1);
  const safeMaxDate = maxTicketDate ?? new Date();
  const [search, setSearch] = useState(searchParams.get("tid") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("tid") || "");
  const [isLoading, setIsLoading] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const searchInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const [statusSummaryData, setStatusSummaryData] = useState([]);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [userStatusSummaryData, setUserStatusSummaryData] = useState({ waiting: 0, open: 0, mytickets: 0 });
  const [allModules, setAllModules] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [priority, setPriority] = useState(null);
  const [module, setModule] = useState([]);
  const [showBadges, setShowBadges] = useState(true);
  const [activeTab, setActiveTab] = useState("all"); // "all" or "scheduler"
  const [showSchedulerModal, setShowSchedulerModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [schedulerRefreshKey, setSchedulerRefreshKey] = useState(0);
  const navigate = useNavigate();


  const handleTicketClick = useCallback((ticket) => {
    setSelectedTicketId(ticket?.id);
    setSelectedTicketChildId(ticket?.child_id);
    const idx = tickets.findIndex(t => t.id === ticket?.id);
    setTicketViewIndex(idx);
  }, [tickets]);

  const closeDetails = useCallback(() => {
    setSelectedTicketId(null);
    setSelectedTicketChildId(null);
    setTicketViewIndex(-1);
  }, []);

  const handleNextTicket = useCallback(() => {
    const nextIdx = Math.min(tickets.length - 1, ticketViewIndex + 1);
    if (nextIdx !== ticketViewIndex) {
      const nextTicket = tickets[nextIdx];
      setSelectedTicketId(nextTicket.id);
      setSelectedTicketChildId(nextTicket.child_id);
      setTicketViewIndex(nextIdx);
    }
  }, [tickets, ticketViewIndex]);

  const handlePrevTicket = useCallback(() => {
    const prevIdx = Math.max(0, ticketViewIndex - 1);
    if (prevIdx !== ticketViewIndex) {
      const prevTicket = tickets[prevIdx];
      setSelectedTicketId(prevTicket.id);
      setSelectedTicketChildId(prevTicket.child_id);
      setTicketViewIndex(prevIdx);
    }
  }, [tickets, ticketViewIndex]);

  useEffect(() => {
    document.title = "Enova E - Ticketing";
  }, []);

  useEffect(() => {
    localStorage.setItem("ticketRowsPerPage", String(rowsPerPage));
  }, [rowsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (searchInputRef.current && [2, 17].includes(Number(user?.role_id))) {
      searchInputRef.current.focus();
    }
  }, []);

  // Load paginated dataset; searching, filtering and pagination are now server-side.
  const loadTickets = useCallback(
    async (page = 1, currentRowsPerPage = rowsPerPage, signal) => {
      setIsLoading(true);
      try {
        const offset = (page - 1) * currentRowsPerPage;
        const res = await api.get("/ticket", {
          params: {
            child_id: [2, 17].includes(Number(user?.role_id)) ? (user?.child_id ?? "") : (selectedCustomerChildId || ""),
            limit: currentRowsPerPage,
            offset: offset,
            search: debouncedSearch,
            statusId: selectedStatus || "",
            userStatus: userStatus || "",
            clientId: selectedClientId || "",
            priorityId: priority || "",
            module: module.join(','),
            fromDate: fromDate || "",
            toDate: toDate || "",
          },
          ...(signal && { signal }),
        });
        const data = res.data;
        const raw = (data?.data != null)
          ? (Array.isArray(data.data) ? data.data : [data.data])
          : [];
        if (data && data.status === true) {
          const mapped = raw.map((t) => {
            return {
              id: t.id,
              collegeName: t.college_name,
              client: t.client_name,
              client_key: t.client_key,
              module: t.module_name,
              title: t.title,
              description: t.description,
              priority: t.priority,
              status: t.status,
              status_id: t.status_id,
              assign_user_id: t.assign_user_id,
              created_by_user_id: t.created_by_user_id,
              assignedTo: t.assigned_to,
              AssignedTo_designation_id: t.AssignedTo_designation_id,
              ReportedBy_designation_id: t.ReportedBy_designation_id,
              created: t.created_date,
              updated: t.updated_date,
              involves_user: t.involves_user,
              comment_count: t.comment_count,
              child_id: t.child_id,
              issue_type: t.issue_type,
              customer_name: t.customer_name,
              priority_id: t.priority_id,
              module_id: t.module,
            };
          });
          setTickets(mapped);
          setTotalTickets(data.total || mapped.length);

          // One-time setup for date range if not already set or for initial load
          if (!fromDate && !toDate && mapped.length > 0) {
            const dates = mapped.map((t) => t.created).filter(Boolean);
            if (dates.length) {
              setMinTicketDate(new Date(Math.min(...dates.map((d) => new Date(d)).map(Number))));
              setMaxTicketDate(new Date(Math.max(...dates.map((d) => new Date(d)).map(Number))));
            }
          }
        } else {
          setTickets([]);
          setTotalTickets(0);
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "AbortError") return;
        console.error("Fetch failed:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [user?.child_id, user?.role_id, rowsPerPage, debouncedSearch, selectedStatus, userStatus, selectedClientId, selectedCustomerChildId, priority, module, fromDate, toDate]
  );

  const loadSummaries = useCallback(async () => {
    setIsSummaryLoading(true);
    try {
      const [statusRes, userRes] = await Promise.all([
        api.get("/ticket/status-summary", {
          params: {
            child_id: user?.child_id ?? "",
            clientId: selectedClientId || ""
          }
        }),
        api.get("/ticket/user-status-summary", {
          params: {
            child_id: user?.child_id ?? "",
            clientId: selectedClientId || ""
          }
        })
      ]);

      if (statusRes.data?.status) {
        let data = statusRes.data.data;
        // For customers, merge all internal pipeline counts (7-11, 13, 14-20) into "IN process" (Status 4)
        if ([2, 17].includes(Number(user?.role_id))) {
          const inProcessItem = data.find((s) => Number(s.status_id) === 4);
          const internalIds = Number(user?.role_id) === 17
            ? [7, 8, 9, 10, 11, 14, 15, 16, 17, 18, 19, 20]
            : [7, 8, 9, 10, 11, 13, 14, 15, 16, 17, 18, 19, 20];
          const internalCountsBatch = data
            .filter((s) => internalIds.includes(Number(s.status_id)))
            .reduce((sum, s) => sum + Number(s.count || 0), 0);

          if (inProcessItem) {
            inProcessItem.count = Number(inProcessItem.count || 0) + internalCountsBatch;
          }
          data = data.filter((s) => !internalIds.includes(Number(s.status_id)));
        }
        setStatusSummaryData(data);
      }

      if (userRes.data?.status) {
        setUserStatusSummaryData(userRes.data.data || { waiting: 0, open: 0, mytickets: 0 });
      }
    } catch (err) {
      console.error("Summary fetch failed:", err);
    } finally {
      setIsSummaryLoading(false);
    }
  }, [user?.child_id, user?.role_id, selectedClientId]);

  const loadModules = useCallback(async () => {
    try {
      const res = await api.get("/ticket/get-modules", {
        params: { child_id: [2, 17].includes(Number(user?.role_id)) ? (user?.child_id ?? "") : (selectedCustomerChildId || "") }
      });
      if (res.data?.status) {
        setAllModules(res.data.data);
      }
    } catch (err) {
      console.error("Failed to load modules:", err);
    }
  }, [user?.child_id, user?.role_id, selectedCustomerChildId]);


  useEffect(() => {
    if (!user) return;
    loadSummaries();
    loadModules();
  }, [loadSummaries, loadModules, user]);

  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    loadTickets(currentPage, rowsPerPage, controller.signal);
    return () => controller.abort();
  }, [loadTickets, user, currentPage, rowsPerPage]);

  useEffect(() => {
    const handleTicketUpdated = () => {
      loadTickets(currentPage, rowsPerPage);
      loadSummaries();
    };
    window.addEventListener('ticket-updated', handleTicketUpdated);
    return () => window.removeEventListener('ticket-updated', handleTicketUpdated);
  }, [loadTickets, loadSummaries, currentPage, rowsPerPage]);

  const userId = user?.id ?? user?.user_id ?? null;
  const roleId = Number(user?.role_id) || 0;

  const filteredTickets = useMemo(() => {
    return tickets;
  }, [tickets]);


  const statusSummary = useMemo(() => {
    return statusSummaryData.map((s) => ({
      status_id: Number(s.status_id),
      label: getStatusLabel(Number(s.status_id), s.status_name),
      count: Number(s.count),
      color: getStatusColor(Number(s.status_id)),
      text: getStatusTextColor(Number(s.status_id)),
      icon: getStatusIcon(Number(s.status_id)),
    }));
  }, [statusSummaryData]);

  const userStatusCounts = useMemo(() => {
    return userStatusSummaryData;
  }, [userStatusSummaryData]);




  const collegeName = useMemo(() => {
    if (!selectedClientId || !tickets.length) return " - Overall";
    const first = tickets.find((t) => t.client_key === selectedClientId);
    if (!first) return " - Overall";
    return " - " + String((first.client || "") + " (" + (first.collegeName || "") + ")").toUpperCase();
  }, [tickets, selectedClientId]);


  const priorityOptions = useMemo(() => {
    return STATIC_PRIORITY_OPTIONS;
  }, []);

  const moduleOptions = useMemo(() => {
    return allModules.map((m) => ({
      value: String(m.id),
      label: m.module_name,
    }));
  }, [allModules]);


  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return "Date";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy");
    } catch {
      return "Date";
    }
  };

  const goToPrevDay = () => {
    const d = fromDate ? new Date(fromDate) : new Date();
    d.setDate(d.getDate() - 1);
    const newDate = format(d, "yyyy-MM-dd");
    setFromDate(newDate);
    setToDate(newDate);
  };

  const goToNextDay = () => {
    const d = toDate ? new Date(toDate) : new Date();
    d.setDate(d.getDate() + 1);
    const today = new Date();
    if (d > today) return;
    const newDate = format(d, "yyyy-MM-dd");
    setFromDate(newDate);
    setToDate(newDate);
  };

  const goToToday = () => {
    const today = format(new Date(), "yyyy-MM-dd");
    setFromDate(today);
    setToDate(today);
  };

  const statusCounts = useMemo(
    () => ({
      total: totalTickets,
      open: 0,
      resolved: 0,
      canceled: 0
    }),
    [totalTickets]
  );

  const handleFilterClick = useCallback((key) => {
    setSearch("");
    setModule([]);
    setPriority(null);
    setFromDate("");
    setToDate("");
    setSelectedStatus(null);
    setSelectedCustomerChildId(null);
    setUserStatus((prev) => (prev === key ? null : key));
    setCurrentPage(1);
  }, []);

  const applyFilter = useCallback(({ status = null, user = null }) => {
    setSearch("");
    setModule([]);
    setPriority(null);
    setFromDate("");
    setToDate("");
    setSelectedStatus(status);
    setUserStatus(user);
    setSelectedCustomerChildId(null);
    setCurrentPage(1);
  }, []);

  const statusOptions = useMemo(() => {
    const roleId = Number(user?.role_id);
    const designationId = Number(user?.designation_id ?? 0);
    const userDesig = String(user?.designation_name || '').toLowerCase();
    const userRoleName = String(user?.role_name || '').toLowerCase();
    const isVerticalHead = (
      [25, 26, 42, 43, 44, 63].includes(designationId) ||
      userDesig.includes('vertical head') ||
      userDesig.includes('project manager') ||
      userRoleName.includes('vertical head') ||
      userRoleName.includes('project manager')
    );
    const isClientAdvocate = (
      roleId === 3 ||
      userRoleName.includes('client advocate') ||
      userDesig.includes('client advocate')
    );
    const isAdvocateOrHeadOrAdmin = roleId === 1 || isClientAdvocate || isVerticalHead;

    return STATIC_STATUS_OPTIONS.filter(opt => {
      if (roleId === 17) {
        return ["4", "3", "12", "13", "5"].includes(String(opt.value));
      }
      if (roleId === 2 && ["8", "9", "10", "13", "14", "15", "16", "17", "18", "19", "20"].includes(opt.value)) return false;
      // Client Advocate: hide Code Review Request (14) and Production Move Request (17)
      if (roleId === 3 && ["14", "17"].includes(opt.value)) return false;
      // If internal client is selected (108), hide Pending Customer Verification & Confirmation (12)
      if (String(selectedClientId) === "108" && opt.value === "12") return false;
      // If external client is selected, hide Pending verification & closure (3) EXCEPT for Vertical Heads, Client Advocates, Admin, and Client Admin (17)
      if (selectedClientId && String(selectedClientId) !== "108" && opt.value === "3" && !isAdvocateOrHeadOrAdmin && roleId !== 17) return false;
      return true;
    });
  }, [user?.role_id, user?.designation_id, user?.designation_name, user?.role_name, selectedClientId]);

  useEffect(() => {
    if (tickets.length > 0 && ticketIdFromUrl) {
      const matched = tickets.find(
        (t) => String(t.id).trim() === String(ticketIdFromUrl).trim()
      );

      if (matched) {
        handleTicketClick(matched);

        // Remove the tid param from URL
        navigate("/tickets", { replace: true });
      }
    }
  }, [tickets, ticketIdFromUrl]);

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-lg text-gray-600">
        Loading user information...
      </div>
    );
  }

  return (
    <Layout noSidebar={![2, 17].includes(Number(user?.role_id)) ? false : true}>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="p-2 bg-neutral-light min-h-full relative ">
        <div className="bg-white rounded-xl shadow px-3 py-2">
          <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {/* Title */}
            <div className="flex flex-col sm:flex-col lg:flex-row lg:items-center gap-2 flex-1">
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-md md:text-lg xl:text-xl font-medium  truncate">
                  Ticket Dashboard {collegeName}
                </h2>

                <button
                  onClick={() => setShowBadges(!showBadges)}
                  className="sm:hidden text-sm text-primary underline flex items-center gap-1"
                >
                  {showBadges ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>

              {/* Tabs Section - Admin & Project Manager & Manager: Training & Development */}
              {(Number(user?.role_id) === 1 || [25, 44, 63].includes(Number(user?.designation_id))) && (
                <div className="flex bg-gray-100/50 p-1 rounded-lg w-fit">
                  <button
                    onClick={() => setActiveTab("all")}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${activeTab === 'all'
                      ? 'bg-white text-red-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    All Tickets
                  </button>
                  <button
                    onClick={() => setActiveTab("scheduler")}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${activeTab === 'scheduler'
                      ? 'bg-white text-red-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Ticket Scheduler
                  </button>
                </div>
              )}
            </div>
          </div>

          {activeTab === 'all' ? (
            <>

              {!isSummaryLoading ? (
                <TicketDashboardSummary
                  statusSummary={statusSummary}
                  onStatusClick={(statusId) => {
                    if (statusId === "open_overall_clicked") {
                      handleFilterClick("open");
                    } else {
                      applyFilter({ status: statusId });
                    }
                  }}
                  selectedStatusId={selectedStatus}
                  userStatus={userStatus}
                />
              ) : (
                <div className="w-full h-32 flex items-center justify-center bg-gray-50 rounded-xl animate-pulse text-gray-400 text-sm font-medium">
                  Loading dashboard summaries...
                </div>
              )}

              {/* Buttons */}
              <div className="flex flex-wrap justify-between items-start gap-3 mb-3 mt-3">
                <div
                  className={`flex flex-wrap gap-2  ${showBadges ? "" : "hidden"} `}
                >
                  {[
                    {
                      label: "Waiting on Me",
                      count: userStatusCounts.waiting,
                      color: " text-yellow-800",
                      key: "waiting",
                      note: "Tickets assigned to you that require your action.",
                    },
                    {
                      label: "Open Tickets",
                      count: userStatusCounts.open,
                      color: " text-orange-800",
                      // key: Number(user?.designation_id) === 44 ? "my_open" : "open",
                      key: ([25, 26, 42, 43, 44, 63].includes(Number(user?.designation_id)) || Number(user?.id) === 101) ? "my_open" : "open",
                      note: "Tickets that are new, in progress, or reopened.",
                    },
                    {
                      label: "My Tickets",
                      count: userStatusCounts.mytickets,
                      color: " text-blue-800",
                      key: "mytickets",
                      note: "Tickets you created, updated, or are assigned to.",
                    },
                    (user?.role_id === "1" || [25, 26, 42, 43, 44, 63].includes(Number(user?.designation_id))) && {
                      label: "Follow-ups",
                      count: userStatusCounts.followups,
                      color: " text-purple-800",
                      key: "followups",
                      note: "Internal tickets where you are marked as CC.",
                    },
                  ].filter(Boolean).map(({ label, count, color, key, note }) => {
                    const isActive = userStatus === key;
                    return (
                      <div key={key} className="relative group">
                        <button
                          onClick={() => handleFilterClick(key)}
                          className={`px-2 py-1.5 rounded-full font-medium text-textPrimary text-sm flex items-center gap-2 transition border whitespace-nowrap justify-center md:justify-start ${color}
          ${isActive ? "bg-main text-white" : "hover:shadow-md"}`}
                        >
                          <span>{label}</span>
                          <span className="bg-white text-black px-2 py-0.5 rounded-full text-xs font-semibold">
                            {count}
                          </span>
                        </button>

                        {/* Tooltip */}
                        <div className="absolute bottom-full mb-2 hidden group-hover:block z-50 w-48 text-sm text-white bg-gray-900 rounded-lg px-3 py-2 shadow-lg">
                          {note}
                          <div className="absolute left-4 top-full w-2 h-2 bg-gray-900 rotate-45"></div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-wrap gap-2 justify-end w-full md:w-auto">
                  <button
                    className="bg-red-700 hover:bg-red-800 text-sm text-white px-2 py-1 rounded-md transition font-medium whitespace-nowrap inline-flex items-center gap-2"
                    onClick={() => setShowForm(true)}
                  >
                    <PlusIcon className="w-5 h-5 text-white-200 " />
                    Create Ticket
                  </button>

                  <button
                    onClick={() => loadTickets()}
                    className=" text-sm px-4 py-1 rounded-md transition font-medium whitespace-nowrap inline-flex items-center gap-2 border  "
                  >
                    <ArrowPathIcon className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1">Refresh</span>
                  </button>
                </div>
              </div>

              <div className="mt-3 mb-6">
                <div className="flex flex-wrap items-end gap-x-3 gap-y-4">
                  {/* Rows Per Page */}
                  <div className="w-[80px]">
                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Rows</label>
                    <select
                      value={rowsPerPage || 10}
                      onChange={(e) => setRowsPerPage(Number(e.target.value))}
                      className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 w-full bg-white shadow-sm h-[38px]"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={99999}>All</option>
                    </select>
                  </div>

                  {/* Date Controls (Timesheet style) */}
                  <div className="flex flex-col items-start min-w-fit max-w-full">
                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Created Date Range</label>
                    <div className="flex flex-wrap items-center gap-1">
                      <button
                        onClick={goToPrevDay}
                        className="p-2 border rounded-lg bg-gray-50 hover:bg-gray-100 transition shadow-sm h-[38px] flex items-center justify-center w-9"
                        title="Previous Day"
                      >
                        <FiChevronLeft className="text-gray-600" />
                      </button>

                      <div
                        className="relative flex items-center justify-center p-2 border rounded-lg bg-gray-50 hover:bg-gray-100 transition cursor-pointer shadow-sm h-[38px] min-w-[120px]"
                        onClick={() => fromDateInputRef.current?.showPicker()}
                      >
                        <FiCalendar className="mr-2 text-gray-500 w-4 h-4 hidden sm:block" />
                        <span className="text-sm font-medium text-gray-700">{formatDateForDisplay(fromDate)}</span>
                        <input
                          ref={fromDateInputRef}
                          type="date"
                          className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                        />
                      </div>

                      <span className="text-gray-400 font-bold px-1">→</span>

                      <div
                        className="relative flex items-center justify-center p-2 border rounded-lg bg-gray-50 hover:bg-gray-100 transition cursor-pointer shadow-sm h-[38px] min-w-[120px]"
                        onClick={() => toDateInputRef.current?.showPicker()}
                      >
                        <FiCalendar className="mr-2 text-gray-500 w-4 h-4 hidden sm:block" />
                        <span className="text-sm font-medium text-gray-700">{formatDateForDisplay(toDate)}</span>
                        <input
                          ref={toDateInputRef}
                          type="date"
                          className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </div>

                      <button
                        onClick={goToNextDay}
                        className="p-2 border rounded-lg bg-gray-50 hover:bg-gray-100 transition shadow-sm h-[38px] flex items-center justify-center w-9"
                        title="Next Day"
                      >
                        <FiChevronRight className="text-gray-600" />
                      </button>

                      <button
                        onClick={goToToday}
                        className="px-3 border rounded-lg bg-white hover:bg-gray-50 text-red-600 font-bold text-[10px] uppercase tracking-tighter transition shadow-sm h-[38px] border-red-200"
                      >
                        Today
                      </button>

                      {(fromDate || toDate) && (
                        <button
                          onClick={() => { setFromDate(""); setToDate(""); }}
                          className="p-2 border rounded-lg bg-gray-50 hover:bg-gray-100 transition shadow-sm h-[38px] flex items-center justify-center w-9"
                          title="Reset Dates"
                        >
                          <FiRotateCcw className="text-gray-500 w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Module Filter */}
                  <div className="w-[180px]">
                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Module</label>
                    <Select
                      isMulti
                      name="modules"
                      options={moduleOptions}
                      classNamePrefix="select"
                      className="text-sm shadow-sm"
                      value={moduleOptions.filter((o) => module.includes(o.value))}
                      onChange={(selected) => setModule(selected.map((o) => o.value))}
                      placeholder="All"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '38px',
                          borderRadius: '4px',
                          borderColor: '#d1d5db',
                        })
                      }}
                    />
                  </div>

                  {/* Priority Filter */}
                  <div className="w-[150px]">
                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Priority</label>
                    <Select
                      name="priority"
                      options={priorityOptions}
                      classNamePrefix="select"
                      className="text-sm shadow-sm"
                      value={priorityOptions.find((o) => o.value === priority)}
                      onChange={(opt) => setPriority(opt ? String(opt.value) : null)}
                      placeholder="All Priorities"
                      isClearable
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '38px',
                          borderRadius: '4px',
                          borderColor: '#d1d5db',
                        })
                      }}
                    />
                  </div>

                  {/* Search Filter */}
                  <div className="flex-grow max-w-xs relative bg-white lg:bg-transparent">
                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Search</label>
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="border border-gray-300 rounded-lg px-4 py-2 pr-10 w-full focus:outline-none focus:ring-2 focus:ring-red-500 shadow-sm transition-all h-[38px] text-sm"
                        placeholder="Search tickets..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      {search && (
                        <button
                          onClick={() => {
                            setSearch("");
                            searchInputRef.current?.focus();
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 flex items-center justify-center text-white bg-red-600 rounded-full text-xs shadow-sm transition hover:bg-red-700"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <TicketTable
                tickets={filteredTickets}
                onTicketClick={handleTicketClick}
                isLoading={selectedTicketId ? false : isLoading}
                rowsPerPage={rowsPerPage}
                totalRows={totalTickets}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
                onRowsPerPageChange={(newRows) => {
                  setRowsPerPage(newRows);
                  setCurrentPage(1);
                }}
              />

              {selectedTicketId && (
                <TicketDetails
                  ticketId={selectedTicketId}
                  onClose={closeDetails}
                  priorityOptions={priorityOptions}
                  statusOptions={STATIC_STATUS_OPTIONS}
                  userInfo={user}
                  selectedClientId={selectedClientId}
                  selectedTicketChildId={selectedTicketChildId}
                  onSuccess={() => {
                    setShowForm(false);
                    toast.success("Ticket updated successfully");
                    loadTickets();
                    loadSummaries();
                  }}
                  onNext={handleNextTicket}
                  onPrevious={handlePrevTicket}
                  hasNext={ticketViewIndex < tickets.length - 1}
                  hasPrevious={ticketViewIndex > 0}
                />
              )}
            </>
          ) : null}
        </div>

        {activeTab === 'scheduler' && (
          <div className="mt-4">
            <TicketSchedulerTable
              key={schedulerRefreshKey}
              onEdit={(schedule) => {
                setSelectedSchedule(schedule);
                setShowSchedulerModal(true);
              }}
            />
          </div>
        )}

        {showSchedulerModal && (
          <TicketSchedulerModal
            schedule={selectedSchedule}
            onClose={() => {
              setShowSchedulerModal(false);
              setSelectedSchedule(null);
            }}
            onSave={() => {
              setSchedulerRefreshKey(prev => prev + 1);
            }}
          />
        )}

        {showForm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm px-4"
          >
            <div
              className="relative w-full max-w-4xl bg-white rounded-[20px] shadow-2xl overflow-hidden flex flex-col max-h-[95vh]"
              onClick={(e) => e.stopPropagation()} // stop closing when clicking inside
            >
              {/* Close Button */}
              <button
                onClick={() => setShowForm(false)}
                className="absolute top-5 right-5 w-6 h-6 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-200 hover:text-black transition"
                aria-label="Close"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>

              {/* Header */}
              <div className="p-6 border-b shrink-0">
                <h2 className="text-2xl font-semibold text-center text-red-600">
                  Create New Ticket
                </h2>
                <p className="text-sm text-center text-gray-500 mt-1">
                  Fill in the details below to raise a support request.
                </p>
              </div>

              {/* Form */}
              <div className="px-6 py-4 space-y-6 flex-1 overflow-y-auto">
                <TicketForm
                  onSuccess={() => {
                    toast.success("Ticket created successfully");
                    loadTickets();
                    loadSummaries();
                    setShowForm(false);
                  }}
                  isAdmin={![2, 17].includes(Number(user?.role_id))}
                  priorityOptions={priorityOptions}
                  statusOptions={STATIC_STATUS_OPTIONS}
                  userInfo={user}
                  selectedClientId={selectedClientId}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
